/**
 * PersonalReadyPage — /personal/:slug/ready
 *
 * Mirror of PromReadyPage for type='personal' events. Same launch-button
 * mindset: get the host to share the QR/link RIGHT NOW. No prom-specific
 * copy, no school references — pure consumer language.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import axios from 'axios';
import { CheckCircle, Copy, ArrowRight, WhatsappLogo, Envelope, Eye, Confetti, Flame } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PersonalReadyPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [copied, setCopied] = useState(false);
  const [guestCount, setGuestCount] = useState(0);

  const link = typeof window !== 'undefined' ? `${window.location.origin}/e/${slug}` : '';
  const shareMessage = `Join my event 🎉\nUpload photos, videos & messages here:\n${link}`;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const ev = await axios.get(`${API}/events/slug/${slug}`);
        if (cancelled) return;
        setEvent(ev.data);
        try {
          const stats = await axios.get(`${API}/events/${ev.data.event_id}/public-stats`);
          if (!cancelled) setGuestCount(stats?.data?.guests || 0);
        } catch { /* ignore */ }
      } catch {
        // ignore — page still renders
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      toast.success('Link copied — paste it in your group chat');
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error('Could not copy — long-press to copy manually');
    }
  };

  const shareWhatsApp = () => {
    window.open(`https://wa.me/?text=${encodeURIComponent(shareMessage)}`, '_blank', 'noopener,noreferrer');
  };

  const shareEmail = () => {
    const subject = encodeURIComponent('My event — join here');
    const body = encodeURIComponent(shareMessage);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  return (
    <div className="min-h-screen bg-neutral-950 text-white px-5 py-10" data-testid="personal-ready">
      <div className="max-w-md mx-auto flex flex-col items-center">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45 }}
          className="text-center w-full"
        >
          <div className="w-12 h-12 rounded-full bg-emerald-500/15 flex items-center justify-center mx-auto mb-3">
            <CheckCircle size={24} weight="fill" className="text-emerald-300" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-black mb-2 flex items-center justify-center gap-2" data-testid="personal-ready-title">
            Your event is ready <Confetti size={22} weight="fill" className="text-amber-300" />
          </h1>
          {event?.event_name && <p className="text-white/65 mb-2">{event.event_name}</p>}
          <p className="text-white/85 text-sm sm:text-base mb-3 leading-relaxed">
            Share this with your guests now — they can start uploading instantly
          </p>
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/[0.04] border border-white/8 text-[12px] text-white/70 mb-6">
            {guestCount > 0 ? (
              <><Flame size={12} weight="fill" className="text-amber-300" /> {guestCount} {guestCount === 1 ? 'person' : 'people'} already joined</>
            ) : (
              <>Be the first to share this with your friends</>
            )}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4, delay: 0.1 }}
          className="rounded-2xl bg-white p-4 mb-3 shadow-[0_20px_40px_rgba(194,157,82,0.18)]"
        >
          <img
            src={`${API}/prom/${slug}/qr`}
            alt="Event QR code"
            className="w-56 h-56 block"
            data-testid="personal-ready-qr"
          />
        </motion.div>
        <p className="text-xs text-white/50 mb-6 text-center">
          Guests just scan and start — no apps needed
        </p>

        <button
          onClick={shareWhatsApp}
          className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl text-sm font-black text-black bg-[#25D366] hover:bg-[#1fb858] transition-colors shadow-lg mb-3"
          data-testid="personal-ready-whatsapp"
        >
          <WhatsappLogo size={18} weight="fill" /> Share on WhatsApp <ArrowRight size={14} weight="bold" />
        </button>

        <button
          onClick={onCopy}
          className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl text-sm font-black text-black bg-white hover:bg-neutral-200 transition-colors mb-3"
          data-testid="personal-ready-copy"
        >
          <Copy size={14} weight="bold" /> {copied ? 'Copied — paste into your chat' : 'Copy link'}
        </button>

        <button
          onClick={shareEmail}
          className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl text-sm font-bold text-white/85 border border-white/15 bg-white/[0.04] hover:bg-white/[0.08] transition-colors"
          data-testid="personal-ready-email"
        >
          <Envelope size={14} weight="fill" /> Send via email
        </button>

        <p className="text-[11px] text-white/35 mt-4 text-center break-all w-full px-2">
          {link}
        </p>

        <p className="text-xs text-white/55 mt-6 text-center max-w-xs">
          That&apos;s it — no setup needed. Guests can start using it immediately.
        </p>

        <a
          href={`/e/${slug}`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 text-[12px] text-[#C29D52]/80 hover:text-[#C29D52]/85 mt-4 underline-offset-2 hover:underline"
          data-testid="personal-ready-preview"
        >
          <Eye size={13} weight="fill" /> Preview guest view
        </a>
      </div>
    </div>
  );
}
